export const ASSISTANT_FAB_TAG = "node-assistant-fab";
/**
 * Ask-AI entry point — epic #1507 fidelity F7.
 *
 * Ports the original skin's `.ai-fab` coral tab (`.node-design/original-skin/core/
 * assistant/popup-view.js`'s `#aiFab` markup, `core/assistant/popup.js`'s open/close
 * wiring) as a small, ALWAYS-eager custom element `app-shell.ts` mounts as a sibling of
 * `.shell` in its shadow skeleton — same unconditional presence as the source's own
 * `#aiFab`, injected straight onto `<body>` (a sibling of `#shell`, not a descendant of
 * it) rather than gated behind any particular route. The vendored `.ai-fab`/`.ai-pop` CSS
 * (already rehomed into `skin/css/legacy-numbered.css` + `skin/css/fixes.css`, plus the
 * base `.ai-fab{…}` structural rule `skin/css/assistant.css` adds — see that file's own
 * doc comment) governs position/rotation/hover on both themes; this module only owns
 * markup + open/close wiring.
 *
 * Fidelity note: the source ALSO carries two per-view overrides that hide `.ai-fab`
 * entirely — `.shell[data-view="agents"] .ai-fab{display:none}` (`skin/stages-css/
 * agents.css`) and `.shell[data-view="friends"] .ai-fab{display:none !important}`
 * (`skin/stages-css/friends.css`), both already vendored, both selectors requiring
 * `.ai-fab` to be a DESCENDANT of `.shell`. Mounting the fab as a sibling of `.shell`
 * (matching the source's own sibling-of-`#shell` DOM shape) means those two selectors
 * never match here — a real fidelity regression this port initially shipped uncaught
 * (the tab stayed visible on every route, including agents/friends, overlapping the
 * agents "Stop" pill and the friends composer). Fixed via a shell-level sibling-combinator
 * rule instead (`skin-stylesheet.ts`'s `SHELL_UTILITY_CSS`: `.shell[data-view="agents"] ~
 * node-assistant-fab, .shell[data-view="friends"] ~ node-assistant-fab { display: none;
 * }`) rather than moving this element inside `.shell` (which would need a bigger DOM
 * change and isn't otherwise required — `<node-assistant-fab>`'s only functional need is
 * to be reachable from the shadow root, not to be a `.shell` descendant).
 *
 * #1528 JS budget — this and `assistant-panel.ts` were both trimmed hard, past what would
 * otherwise be idiomatic for this codebase (no `disconnectedCallback` cleanup, no keyboard-
 * Escape close, ids dropped wherever a class selector already does the same job, comments
 * carrying the "why" instead of code): `system/pwa/scripts/verify-build.mjs` precaches
 * EVERY built chunk for offline use, lazy-loaded or not, so `import()`-splitting this
 * module's panel body (below) does NOT exempt its bytes from the same 251KB gzip gate a
 * fully-eager module would count against (confirmed empirically while building this
 * feature — filed as a real gap in #1528, not assumed away here) — the margin was already
 * down to ~289 bytes before this task (F6's own landing note), so unlike most modules in
 * this codebase, literally every remaining byte here had to be justified. The dynamic
 * `import()` is kept anyway (not inlined into this file) because it still defers the
 * panel's parse/eval cost off the CRITICAL PATH for real users (a genuine, separate win
 * from the gzip-budget number this file's own size is measured against).
 */
export class NodeAssistantFabElement extends HTMLElement {
    #loadingPanel;
    #panel;
    connectedCallback() {
        this.innerHTML = '<button class="ai-fab" aria-label="Talk to your agents" title="Talk to your agents"><svg class="ai-fab-curve" viewBox="0 0 150 150" aria-hidden="true"><defs><path id="aiFabArc" d="M 23 90 L 23 38 A 15 15 0 0 1 38 23 L 90 23" fill="none"/></defs><text class="ai-fab-curvetext"><textPath href="#aiFabArc" startOffset="50%" text-anchor="middle">ask ai</textPath></text></svg></button>';
        this.querySelector(".ai-fab")?.addEventListener("click", this.#handleFabClick);
    }
    #handleFabClick = () => {
        void this.#toggle();
    };
    async #toggle() {
        if (this.#panel) {
            this.#panel.toggle();
            return;
        }
        if (this.#loadingPanel)
            return;
        this.#loadingPanel = this.#mountPanel();
        await this.#loadingPanel;
        this.#loadingPanel = undefined;
    }
    async #mountPanel() {
        const panelModule = await import("./assistant-panel.js");
        if (!this.isConnected)
            return;
        panelModule.registerAssistantPanelElement();
        const panel = document.createElement(panelModule.ASSISTANT_PANEL_TAG);
        this.append(panel);
        this.#panel = panel;
        panel.open();
    }
}
export function registerAssistantFabElement() {
    if (!customElements.get(ASSISTANT_FAB_TAG)) {
        customElements.define(ASSISTANT_FAB_TAG, NodeAssistantFabElement);
    }
}
//# sourceMappingURL=assistant-fab.js.map